import { useEffect, useState, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { MapPin, Camera, Loader2, History, CheckCircle2, ThumbsUp, ThumbsDown, Wrench, ArrowRightLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import api, { fileUrl } from '../lib/api';
import { useSync } from '../context/SyncContext';
import CameraCapture from './CameraCapture';
import { SITE_STATUS, POSITIONS, ACTION_LABELS } from '../lib/status';

// Build operator action options from inspection mode + current site state
function buildOps(mode, site) {
  const occupied = !!site.current_campaign_id;
  const warned = ['damaged', 'repair_pending'].includes(site.current_status);
  if (mode === 'install') {
    return [{
      key: 'install', label: occupied ? 'Change Poster' : 'Install Poster', action_type: 'install',
      needsCampaign: true, color: 'bg-blue-600 hover:bg-blue-500', icon: occupied ? ArrowRightLeft : Camera,
      slots: occupied
        ? [{ tag: 'removal', label: 'Old poster — removal proof' }, { tag: 'install', label: 'New poster — install proof' }]
        : [{ tag: 'install', label: 'Installed poster proof' }],
    }];
  }
  if (warned) {
    return [{
      key: 'repair', label: 'Repair Done', action_type: 'repair', color: 'bg-cyan-600 hover:bg-cyan-500',
      icon: Wrench, slots: [{ tag: 'repair', label: 'Repaired proof' }],
    }];
  }
  return [
    {
      key: 'ok', label: 'OK', action_type: occupied ? 'inspect_ok' : 'install', needsCampaign: !occupied,
      color: 'bg-emerald-600 hover:bg-emerald-500', icon: ThumbsUp,
      slots: [{ tag: occupied ? 'inspect' : 'install', label: occupied ? 'Inspection proof' : 'Installed poster proof' }],
    },
    {
      key: 'notok', label: 'Not OK', action_type: 'damage', color: 'bg-rose-600 hover:bg-rose-500',
      icon: ThumbsDown, slots: [{ tag: 'damage', label: 'Damage proof' }],
    },
  ];
}

export default function SiteActionSheet({ siteId, open, onClose, readOnly = false, mode = 'inspect', inspectionId = null }) {
  const { enqueueAction, online } = useSync();
  const [detail, setDetail] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [op, setOp] = useState(null);
  const [campaignId, setCampaignId] = useState('');
  const [notes, setNotes] = useState('');
  const [shots, setShots] = useState({}); // tag -> {blob,name,gps}
  const [camSlot, setCamSlot] = useState(null); // slot being captured
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!siteId) return;
    const [d, c] = await Promise.all([
      api.get(`/sites/${siteId}`),
      api.get(`/sites/${siteId}/eligible-campaigns`).catch(() => ({ data: [] })),
    ]);
    setDetail(d.data);
    setCampaigns(c.data);
    // On a changeover (install mode + already occupied) start with a blank campaign so the operator must pick the NEW one
    const cur = d.data.site.current_campaign_id || '';
    setCampaignId(mode === 'install' && cur ? '' : cur);
  }, [siteId, mode]);

  useEffect(() => {
    if (open && siteId) { setOp(null); setNotes(''); setShots({}); setCamSlot(null); load(); }
  }, [open, siteId, load]);

  const site = detail?.site;
  const cfg = site ? (SITE_STATUS[site.current_status] || SITE_STATUS.not_installed) : null;
  const ops = site ? buildOps(mode, site) : [];

  const submit = async () => {
    if (!op) return;
    const missing = op.slots.filter((s) => !shots[s.tag]);
    if (missing.length) { toast.error(`Capture required photo: ${missing[0].label}`); return; }
    if (op.needsCampaign && !campaignId) { toast.error('Select a campaign first.'); return; }
    const first = shots[op.slots[0].tag];
    const gps = first?.gps ? { lat: first.gps.lat, lng: first.gps.lng } : null;
    // removal slot (2-photo install) goes to removalFiles; the rest are proof files
    const removalFiles = op.slots.filter((s) => s.tag === 'removal').map((s) => shots[s.tag]);
    const files = op.slots.filter((s) => s.tag !== 'removal').map((s) => shots[s.tag]);
    setSubmitting(true);
    await enqueueAction({
      siteId, action_type: op.action_type,
      campaign_id: op.needsCampaign ? campaignId : (campaignId || null),
      notes, gps, files, removalFiles, inspection_id: inspectionId,
    });
    setSubmitting(false);
    toast.success(online ? 'Saved & syncing proof' : 'Saved locally — will sync when online');
    onClose(true);
  };

  const capturedCount = op ? op.slots.filter((s) => shots[s.tag]).length : 0;
  const stampBase = detail ? [
    `${POSITIONS[site.position_type]} · ${detail.lift?.name}`,
    `${detail.society?.name} · ${detail.tower?.name}`,
  ] : [];

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => !v && onClose(false)}>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-2xl border-t-2 border-border bg-card p-0">
          {!site && <SheetTitle className="sr-only">Site actions</SheetTitle>}
          {site && (
            <>
              <SheetHeader className="sticky top-0 z-10 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
                <div className="flex items-center justify-between">
                  <div>
                    <SheetTitle className="font-head text-xl">{POSITIONS[site.position_type]}</SheetTitle>
                    <p className="text-xs text-muted-foreground">{detail.society?.name} · {detail.tower?.name} · {detail.lift?.name}</p>
                    <SheetDescription className="sr-only">Field operator actions with camera proof, and proof history.</SheetDescription>
                  </div>
                  <span className={`rounded-md border px-2 py-1 text-[10px] font-mono uppercase ${cfg.badge}`}>{cfg.label}</span>
                </div>
              </SheetHeader>

              <Tabs defaultValue={readOnly ? 'history' : 'action'} className="px-4 py-3">
                {!readOnly && (
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="action" data-testid="tab-action">Action</TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history"><History className="mr-1 h-3.5 w-3.5" />History</TabsTrigger>
                  </TabsList>
                )}

                {!readOnly && (
                  <TabsContent value="action" className="mt-4 space-y-4">
                    {['damaged', 'repair_pending'].includes(site.current_status) && (
                      <div data-testid="site-warning" className="flex items-center gap-2 rounded-lg border border-amber-600 bg-amber-950/40 px-3 py-2 text-xs text-amber-300">
                        <ThumbsDown className="h-4 w-4" /> This position was flagged Not OK. Repair and mark as done.
                      </div>
                    )}

                    <div>
                      <p className="overline mb-2 text-muted-foreground">{mode === 'install' ? 'Install Action' : 'Inspection Result'}</p>
                      <div className={`grid gap-2 ${ops.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        {ops.map((o) => {
                          const Icon = o.icon;
                          return (
                            <button key={o.key} data-testid={`op-${o.key}-button`} onClick={() => { setOp(o); setShots({}); }}
                              className={`flex items-center justify-center gap-2 rounded-lg px-3 py-3 text-sm font-semibold text-white transition ${o.color} ${op?.key === o.key ? 'ring-2 ring-offset-2 ring-offset-card ring-white/80' : 'opacity-90'}`}>
                              <Icon className="h-4 w-4" /> {o.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {op && (
                      <>
                        {op.needsCampaign && (
                          <div>
                            <p className="overline mb-1.5 text-muted-foreground">Campaign / Client</p>
                            <Select value={campaignId} onValueChange={setCampaignId}>
                              <SelectTrigger data-testid="campaign-select"><SelectValue placeholder="Choose mapped campaign" /></SelectTrigger>
                              <SelectContent>
                                {campaigns.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No campaigns mapped to this site</div>}
                                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.client_name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <div>
                          <p className="overline mb-1.5 text-muted-foreground">
                            Proof Photos ({capturedCount}/{op.slots.length})
                          </p>
                          <div className="space-y-2">
                            {op.slots.map((slot) => {
                              const shot = shots[slot.tag];
                              return (
                                <button key={slot.tag} data-testid={`capture-slot-${slot.tag}`} onClick={() => setCamSlot(slot)}
                                  className={`flex w-full items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-left text-sm transition ${shot ? 'border-emerald-600 bg-emerald-950/30' : 'border-dashed border-border hover:border-primary'}`}>
                                  {shot ? (
                                    <img src={URL.createObjectURL(shot.blob)} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" />
                                  ) : (
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-secondary"><Camera className="h-5 w-5 text-muted-foreground" /></span>
                                  )}
                                  <span className="flex-1">
                                    <span className="block font-medium">{slot.label}</span>
                                    <span className="text-xs text-muted-foreground">{shot ? 'Captured · tap to retake' : 'Tap to open camera'}</span>
                                  </span>
                                  {shot && <Check className="h-5 w-5 text-emerald-400" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <Textarea data-testid="action-notes" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-[56px]" />

                        <Button data-testid="submit-action-btn" onClick={submit} disabled={submitting} className="h-12 w-full text-base font-semibold">
                          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-5 w-5" />}
                          Save Proof · {op.label}
                        </Button>
                      </>
                    )}
                  </TabsContent>
                )}

                <TabsContent value="history" className="mt-4 space-y-3">
                  {detail.history.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No history yet for this site.</p>}
                  {detail.history.map((h) => (
                    <div key={h.id} data-testid={`history-item-${h.id}`} className="rounded-lg border border-border bg-background/60 p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-head font-semibold" style={{ color: (SITE_STATUS[h.new_status] || {}).dot }}>{ACTION_LABELS[h.action_type] || h.action_type}{h.tag === 'removal' ? ' (removal)' : ''}</span>
                        <span className="font-mono text-[10px] text-muted-foreground">{(h.timestamp || h.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">By {h.operator_name || 'operator'}</p>
                      {h.address && <p className="mt-1 flex items-start gap-1 text-[11px] text-muted-foreground"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{h.address}</p>}
                      {h.gps?.lat && <p className="font-mono text-[10px] text-muted-foreground">{h.gps.lat.toFixed(5)}, {h.gps.lng.toFixed(5)}</p>}
                      {h.notes && <p className="mt-1 text-xs">{h.notes}</p>}
                      {h.file_ids?.length > 0 && (
                        <div className="mt-2 flex gap-2 overflow-x-auto">
                          {h.file_ids.map((fid) => <img key={fid} src={fileUrl(fid)} alt="" className="h-16 w-16 rounded-md object-cover" />)}
                        </div>
                      )}
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CameraCapture
        open={!!camSlot}
        title={camSlot?.label || 'Capture Proof'}
        stampLines={camSlot ? [camSlot.label, ...stampBase] : stampBase}
        onClose={() => setCamSlot(null)}
        onCapture={(shot) => { setShots((p) => ({ ...p, [camSlot.tag]: shot })); setCamSlot(null); }}
      />
    </>
  );
}
